import type { Component, Tag } from '../../services/components.service.js';

export type CatalogPackageKind = 'all' | 'primary' | 'addon';
export type CatalogComponentType = 'agent' | 'context' | 'rule' | 'rule-preset' | 'skill' | 'template' | 'workflow' | 'component';
export type CatalogMatchedField = 'filepath' | 'name' | 'description' | 'tag' | 'type' | 'ide';

export interface CatalogPackageOption extends Tag {
    kind: CatalogPackageKind;
    targetLabel?: string;
}

export interface CatalogSearchOptions {
    query?: string;
    packageTag?: string;
}

export interface CatalogSearchResult {
    component: Component;
    score: number;
    matchedFields: CatalogMatchedField[];
    type: CatalogComponentType;
    packageTags: string[];
    targetLabel?: string;
}

interface ParsedQuery {
    textTerms: string[];
    packageFilters: string[];
    typeFilters: string[];
    ideFilters: string[];
}

const LEGACY_PRIMARY_PACKAGES = ['core-v3', 'core-v2', 'hive-v2', 'core', 'hive-v1'];
const PRIMARY_PACKAGE_ORDER = LEGACY_PRIMARY_PACKAGES;
const RECOMMENDED_PACKAGE_ORDER = ['core', 'core-v2', 'hive-v2'];
const DEFAULT_SUGGESTIONS = ['core-v2', 'hive', 'skill', 'workflow'];

export function buildPackageOptions(tags: Tag[], components: Component[]): CatalogPackageOption[] {
    const primary = tags
        .filter(isPrimaryPackage)
        .sort((a, b) => {
            const aIndex = PRIMARY_PACKAGE_ORDER.indexOf(a.tag);
            const bIndex = PRIMARY_PACKAGE_ORDER.indexOf(b.tag);
            return (aIndex < 0 ? Number.MAX_SAFE_INTEGER : aIndex)
                - (bIndex < 0 ? Number.MAX_SAFE_INTEGER : bIndex)
                || a.tag.localeCompare(b.tag);
        });

    const addons = tags
        .filter((tag) => !isPrimaryPackage(tag))
        .sort((a, b) => a.tag.localeCompare(b.tag));

    return [
        { tag: 'All', count: components.length, kind: 'all' },
        ...primary.map((tag) => toCatalogPackage(tag, 'primary')),
        ...addons.map((tag) => toCatalogPackage(tag, 'addon')),
    ];
}

function isPrimaryPackage(tag: Tag): boolean {
    if (tag.kind === 'primary' || tag.kind === 'addon') return tag.kind === 'primary';
    return LEGACY_PRIMARY_PACKAGES.includes(tag.tag);
}

export function getSelectedPackageTags(
    selectedPaths: string[],
    components: Component[],
    tags: Tag[],
): string[] {
    const selected = new Set(selectedPaths);
    const packageTags = new Set(tags.map((tag) => tag.tag));
    const result = new Set<string>();

    for (const component of components) {
        if (!selected.has(component.filepath)) continue;
        for (const tag of component.tags ?? []) {
            if (packageTags.has(tag)) result.add(tag);
        }
    }

    return [...result];
}

export function getRecommendedPackageOptions(packageOptions: CatalogPackageOption[]): CatalogPackageOption[] {
    const byTag = new Map(packageOptions.map((option) => [option.tag, option]));
    return RECOMMENDED_PACKAGE_ORDER
        .map((tag) => byTag.get(tag))
        .filter((option): option is CatalogPackageOption => Boolean(option));
}

export function getComponentType(filepath: string): CatalogComponentType {
    if (filepath.startsWith('agents/')) return 'agent';
    if (filepath.startsWith('context/')) return 'context';
    if (filepath.startsWith('rule-presets/')) return 'rule-preset';
    if (filepath.startsWith('rules/')) return 'rule';
    if (filepath.startsWith('skills/')) return 'skill';
    if (filepath.startsWith('templates/')) return 'template';
    if (filepath.startsWith('workflows/')) return 'workflow';
    return 'component';
}

export function searchCatalogComponents(
    components: Component[],
    tags: Tag[],
    options: CatalogSearchOptions = {},
): CatalogSearchResult[] {
    const parsed = parseQuery(options.query || '');
    const packageTag = options.packageTag && options.packageTag !== 'All' ? options.packageTag : undefined;
    const tagConfigByName = new Map(tags.map((tag) => [tag.tag, tag]));

    return components
        .map((component) => scoreComponent(component, parsed, tagConfigByName, packageTag))
        .filter((result): result is CatalogSearchResult => Boolean(result))
        .sort((a, b) => b.score - a.score || a.component.filepath.localeCompare(b.component.filepath));
}

export function getVisibleWindow(total: number, cursorIndex: number, windowSize: number): { start: number; end: number } {
    if (total <= 0 || windowSize <= 0) return { start: 0, end: 0 };
    const safeCursor = clamp(cursorIndex, 0, total - 1);
    const size = Math.min(windowSize, total);
    const start = clamp(safeCursor - size + 1, 0, total - size);
    return { start, end: start + size };
}

export function getNoResultsSuggestion(query: string): string {
    const trimmed = query.trim();
    if (!trimmed) {
        return `Try ${DEFAULT_SUGGESTIONS.map((item) => `"${item}"`).join(', ')}.`;
    }

    if (trimmed.includes(':')) {
        return 'Try pkg:core-v2, type:skill, type:workflow, ide:claude, or clear one filter.';
    }

    return `No exact match. Try ${DEFAULT_SUGGESTIONS.map((item) => `"${item}"`).join(', ')} or search by filepath.`;
}

function toCatalogPackage(tag: Tag, kind: CatalogPackageKind): CatalogPackageOption {
    const targetLabel = tag.defaultTarget?.join(', ');
    return {
        ...tag,
        kind,
        ...(targetLabel ? { targetLabel } : {}),
    };
}

function parseQuery(query: string): ParsedQuery {
    const parsed: ParsedQuery = {
        textTerms: [],
        packageFilters: [],
        typeFilters: [],
        ideFilters: [],
    };

    for (const rawToken of query.trim().split(/\s+/).filter(Boolean)) {
        const token = normalize(rawToken);
        const separatorIndex = token.indexOf(':');
        if (separatorIndex > 0) {
            const prefix = token.slice(0, separatorIndex);
            const value = token.slice(separatorIndex + 1);
            if (!value) continue;

            if (prefix === 'pkg' || prefix === 'package') {
                parsed.packageFilters.push(value);
                continue;
            }
            if (prefix === 'type') {
                parsed.typeFilters.push(value);
                continue;
            }
            if (prefix === 'ide' || prefix === 'target') {
                parsed.ideFilters.push(value);
                continue;
            }
        }

        parsed.textTerms.push(token);
    }

    return parsed;
}

function scoreComponent(
    component: Component,
    query: ParsedQuery,
    tagConfigByName: Map<string, Tag>,
    packageTag?: string,
): CatalogSearchResult | null {
    const componentTags = component.tags || [];
    const type = getComponentType(component.filepath);
    const packageTags = componentTags.filter((tag) => tagConfigByName.has(tag));
    const targetTags = packageTags
        .map((tag) => tagConfigByName.get(tag))
        .filter((tag): tag is Tag => Boolean(tag?.defaultTarget?.length));
    const targetLabel = targetTags.flatMap((tag) => tag.defaultTarget || []).join(', ');
    const matchedFields = new Set<CatalogMatchedField>();

    if (packageTag && !componentTags.includes(packageTag)) return null;
    if (query.packageFilters.length > 0 && !query.packageFilters.every((filter) => componentTags.some((tag) => normalize(tag).includes(filter)))) {
        return null;
    }
    if (query.typeFilters.length > 0 && !query.typeFilters.every((filter) => type.includes(filter))) {
        return null;
    }
    if (query.typeFilters.length > 0) {
        matchedFields.add('type');
    }
    if (query.ideFilters.length > 0) {
        const targets = targetTags.flatMap((tag) => tag.defaultTarget || []).map(normalize);
        if (!query.ideFilters.every((filter) => targets.some((target) => target.includes(filter)))) return null;
        matchedFields.add('ide');
    }

    let score = 0;
    for (const term of query.textTerms) {
        const termScore = scoreTerm(component, componentTags, type, term, matchedFields);
        if (termScore === 0) return null;
        score += termScore;
    }

    if (query.textTerms.length === 0) {
        score += 1;
    }

    if (PRIMARY_PACKAGE_ORDER.some((tag) => componentTags.includes(tag))) score += 4;
    if (targetLabel) score += 2;
    score += Math.min(component.downloads || 0, 1000) / 1000;

    return {
        component,
        score,
        matchedFields: [...matchedFields],
        type,
        packageTags,
        ...(targetLabel ? { targetLabel } : {}),
    };
}

function scoreTerm(
    component: Component,
    tags: string[],
    type: CatalogComponentType,
    term: string,
    matchedFields: Set<CatalogMatchedField>,
): number {
    const fields: Array<{ field: CatalogMatchedField; value: string; weight: number }> = [
        { field: 'filepath', value: component.filepath, weight: 50 },
        { field: 'name', value: component.name, weight: 40 },
        { field: 'tag', value: tags.join(' '), weight: 35 },
        { field: 'type', value: type, weight: 30 },
        { field: 'description', value: component.description || '', weight: 20 },
    ];

    let best = 0;
    for (const field of fields) {
        const value = normalize(field.value);
        let fieldScore = 0;
        if (value.includes(term)) {
            fieldScore = field.weight;
            if (value === term) fieldScore += 20;
            if (value.startsWith(term)) fieldScore += 10;
        } else if (isSubsequence(term, value)) {
            fieldScore = Math.max(1, Math.floor(field.weight / 4));
        }

        if (fieldScore > 0) {
            matchedFields.add(field.field);
            best = Math.max(best, fieldScore);
        }
    }

    return best;
}

function normalize(value: string): string {
    return value.toLowerCase().replace(/[_/.-]+/g, ' ').trim();
}

function isSubsequence(needle: string, haystack: string): boolean {
    if (needle.length < 2) return false;
    let index = 0;
    for (const char of haystack) {
        if (char === needle[index]) index++;
        if (index === needle.length) return true;
    }
    return false;
}

function clamp(value: number, min: number, max: number): number {
    return Math.min(Math.max(value, min), max);
}
